import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin, Navigation, CheckCircle2, AlertCircle, Route, AlertTriangle } from 'lucide-react';
import { onsiteApiFetch } from '../config/api.js';
import type { OnsiteJob } from '../../../api/src/types/onsite-types.js';

type GpsState = 'idle' | 'loading' | 'success' | 'error';
type CheckInEvent = 'departed' | 'arrived';

interface CheckInPayload {
  job_id: string;
  event: CheckInEvent;
  latitude: number;
  longitude: number;
  distance_from_site_meters?: number;
  force_override?: boolean;
}

interface GeofenceError {
  distance_meters: number;
  threshold_meters: number;
  require_field: string;
}

export default function CheckInPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [gpsState, setGpsState] = useState<GpsState>('idle');
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [geofenceInfo, setGeofenceInfo] = useState<GeofenceError | null>(null);
  const [pendingEvent, setPendingEvent] = useState<CheckInEvent | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['job', id],
    queryFn: () => onsiteApiFetch<{ job: OnsiteJob }>(`/api/v1/onsite/jobs/${id}`),
    enabled: !!id,
  });

  const checkIn = useMutation({
    mutationFn: async (payload: CheckInPayload) => {
      const base = import.meta.env.VITE_ONSITE_API_URL ?? 'https://api.theonestack.com';
      const res = await fetch(`${base}/api/v1/onsite/checkins`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        window.location.href = 'https://app.theonestack.com/login?redirect=onsite';
        throw new Error('Not authenticated');
      }
      if (res.status === 400) {
        const body = await res.json().catch(() => ({}));
        if (body?.require_field === 'force_override') {
          setGeofenceInfo({
            distance_meters: body.distance_meters ?? 0,
            threshold_meters: body.threshold_meters ?? 0,
            require_field: 'force_override',
          });
          throw new Error('GEOFENCE');
        }
        throw new Error(body?.error ?? 'Check-in failed');
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error ?? `Request failed: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (_d, payload) => {
      qc.invalidateQueries({ queryKey: ['job', id] });
      if (payload.event === 'arrived') {
        navigate(`/jobs/${id}`, { replace: true });
      } else {
        // 'departed' — stay here with feedback
        setPendingEvent(null);
      }
    },
    onError: e => {
      if ((e as Error).message !== 'GEOFENCE') {
        setGenericError((e as Error).message);
      }
    },
  });

  useEffect(() => {
    setGpsState('loading');
    navigator.geolocation.getCurrentPosition(
      pos => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsState('success');
      },
      err => {
        setGpsError(err.message);
        setGpsState('error');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const job = data?.job;

  function calcDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371e3;
    const p1 = lat1 * Math.PI / 180;
    const p2 = lat2 * Math.PI / 180;
    const dp = (lat2 - lat1) * Math.PI / 180;
    const dl = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dp/2)**2 + Math.cos(p1)*Math.cos(p2)*Math.sin(dl/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }

  function buildPayload(event: CheckInEvent, forceOverride = false): CheckInPayload | null {
    if (!coords || !id) return null;
    const distanceMeters = (job?.site_latitude && job?.site_longitude)
      ? calcDistance(coords.lat, coords.lng, job.site_latitude, job.site_longitude)
      : undefined;

    return {
      job_id: id,
      event,
      latitude: coords.lat,
      longitude: coords.lng,
      distance_from_site_meters: distanceMeters ? Math.round(distanceMeters) : undefined,
      ...(forceOverride && { force_override: true }),
    };
  }

  async function handleEvent(event: CheckInEvent) {
    const payload = buildPayload(event);
    if (!payload) return;
    setPendingEvent(event);
    setGenericError(null);
    setGeofenceInfo(null);
    checkIn.mutate(payload);
  }

  async function handleConfirmOverride() {
    if (!pendingEvent) return;
    const payload = buildPayload(pendingEvent, true);
    if (!payload) return;
    setGeofenceInfo(null);
    checkIn.mutate(payload);
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 p-6">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/20">
        <MapPin className="h-10 w-10 text-primary" />
      </div>

      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">GPS Check-In</h1>
        {job && <p className="mt-1 text-muted-foreground">{job.customer_name} · {job.customer_address}</p>}
      </div>

      {/* GPS status */}
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-4">
        {gpsState === 'loading' && (
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">Getting your location…</p>
          </div>
        )}
        {gpsState === 'success' && coords && (
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-400" />
            <div>
              <p className="text-sm font-medium text-green-400">Location acquired</p>
              <p className="text-xs text-muted-foreground">
                {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
              </p>
            </div>
          </div>
        )}
        {gpsState === 'error' && (
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div>
              <p className="text-sm font-medium text-red-400">Location unavailable</p>
              <p className="text-xs text-muted-foreground">{gpsError}</p>
            </div>
          </div>
        )}
      </div>

      {/* Geofence warning */}
      {geofenceInfo && (
        <div className="w-full max-w-sm rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 mt-0.5 text-amber-400 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-300">Outside geofence</p>
              <p className="mt-1 text-sm text-amber-200">
                You are {Math.round(geofenceInfo.distance_meters)} meters from the job site
                (threshold: {geofenceInfo.threshold_meters}m). Check in anyway?
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => { setGeofenceInfo(null); setPendingEvent(null); }}
                  className="flex-1 rounded-lg border border-border py-1.5 text-xs text-muted-foreground hover:bg-background"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmOverride}
                  disabled={checkIn.isPending}
                  className="flex-1 rounded-lg bg-amber-600 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generic error */}
      {genericError && !geofenceInfo && (
        <div className="w-full max-w-sm rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
          {genericError}
        </div>
      )}

      <div className="flex w-full max-w-sm flex-col gap-3">
        <button
          onClick={() => handleEvent('departed')}
          disabled={gpsState !== 'success' || checkIn.isPending}
          className="flex items-center justify-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 text-base font-semibold text-primary hover:bg-primary/20 disabled:opacity-50"
        >
          {checkIn.isPending && pendingEvent === 'departed' ? (
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          ) : (
            <>
              <Route className="h-5 w-5" />
              Start Route
            </>
          )}
        </button>

        <button
          onClick={() => handleEvent('arrived')}
          disabled={gpsState !== 'success' || checkIn.isPending}
          className="flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-4 text-lg font-semibold text-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {checkIn.isPending && pendingEvent === 'arrived' ? (
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <>
              <Navigation className="h-5 w-5" />
              Confirm Arrival
            </>
          )}
        </button>

        <button
          onClick={() => navigate(`/jobs/${id}`, { replace: true })}
          className="rounded-xl border border-border px-4 py-3 text-sm text-muted-foreground hover:bg-secondary"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
